import { AuthApi } from '../auth/authApi';
import { Singleton } from '@/decorators';
import { generateQueryString } from '@/libs';
import type {
  GetSignedApplicationFormLastCreatedRequestDTO,
  GetSignedApplicationFormLastCreatedResponseDTO,
  GetSignedApplicationFormRequestDTO,
  GetSignedApplicationFormResponseDTO,
  SetReviewedRequestDTO,
  SetReviewedResponseDTO,
  SubmitRequestDTO,
  SubmitResponseDTO,
} from './DTOs';
import type { Result } from '@/types/result.interface';
import type { AppError } from '@/types/errors.interface';
import { wrapHttpCall, wrapHttpCallWithAllErrors } from '../utils/apiResultMapper';

/**
 * Review and Submit API (Framework Agnostic)
 * 
 * Handles review and submission operations for application forms.
 */
@Singleton
export class ReviewAndSubmitApi extends AuthApi {
  constructor() {
    super();
  }

  async getSignedApplicationForm(
    body: GetSignedApplicationFormRequestDTO
  ): Promise<Result<GetSignedApplicationFormResponseDTO, AppError>> {
    const queryString = generateQueryString({
      Recreate: body.recreate ?? false,
    });
    return wrapHttpCall(() => this.api.get<GetSignedApplicationFormResponseDTO>(
      `/ApplicationForm/${body.applicationId}/signedapplicationform${queryString}`,
      {
        responseType: 'blob',
      },
    ));
  }

  async getSignedApplicationFormLastCreated(
    body: GetSignedApplicationFormLastCreatedRequestDTO,
  ): Promise<Result<GetSignedApplicationFormLastCreatedResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.get<GetSignedApplicationFormLastCreatedResponseDTO>(
      `/ApplicationForm/${body.applicationId}/signedapplicationform/lastcreated`,
    ));
  }

  async setReviewed(body: SetReviewedRequestDTO): Promise<Result<SetReviewedResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.post<SetReviewedResponseDTO>(
      `/ApplicationForm/${body.applicationId}/reviewed`,
    ));
  }

  /** Fails with every reason the server gave, so the page can list them all. */
  async submit(body: SubmitRequestDTO): Promise<Result<SubmitResponseDTO, AppError[]>> {
    return wrapHttpCallWithAllErrors(() => this.api.post<SubmitResponseDTO>(
      `/ApplicationForm/${body.applicationId}/submit`,
    ));
  }
}
